import React, { useCallback, useMemo, useState } from 'react';
import { Button, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { ADD_USERS_NO_EMAIL } from 'common/constants/user';
import { useResponse } from 'services/hooks';
import useCreateBulkCompanyUsers from 'services/hooks/useCreateBulkCompanyUsers/useCreateBulkCompanyUsers';
import { useCreateBulkUsers } from 'services/hooks/useManageUser/useManageUser';
import { ActionButtonGroupProps } from './ActionButtonGroup.props';
import { actionButtonGroupClasses, ActionButtonGroupRoot } from './ActionButtonGroup.styles';


const ActionButtonGroup = ({
  handleDialogClose,
  createButtonText,
  disableCreate,
  firm,
  selectedCompany,
  newUsers,
  companyList,
  fundList,
}: ActionButtonGroupProps) => {
  const { successNotification, errorNotification } = useResponse();

  const { mutate: createBulkUsers, isLoading: isFirmUsersLoading } = useCreateBulkUsers();
  const { mutate: createBulkCompanyUsers, isLoading: isCompanyUsersLoading } = useCreateBulkCompanyUsers();
  const [skipEmail, setSkipEmail] = useState(false);

  const isLoading = useMemo(
    () => isFirmUsersLoading || isCompanyUsersLoading,
    [isFirmUsersLoading, isCompanyUsersLoading]
  );

  const handleSave = useCallback(async () => {
    if (selectedCompany) {
      createBulkCompanyUsers(
        {
          companyId: selectedCompany.id,
          users: newUsers ?? [],
          sendActivationEmail: !skipEmail,
        },
        {
          onSuccess: response => {
            if (response) {
              handleDialogClose();
              successNotification('Users created successfully');
            }
          },
          onError: () => errorNotification('Error creating users'),
        }
      );
    } else {
      createBulkUsers(
        {
          firm_name: firm.name,
          firm_id: firm.id.toString(),
          users: newUsers ?? [],
          companyList,
          fundList,
          skipEmail,
        },
        {
          onSuccess: response => {
            if (response) {
              handleDialogClose();
              successNotification('Users created successfully');
            }
          },
          onError: () => errorNotification('Error creating users'),
        }
      );
    }
  }, [
    companyList,
    createBulkCompanyUsers,
    createBulkUsers,
    errorNotification,
    firm,
    fundList,
    handleDialogClose,
    newUsers,
    selectedCompany,
    skipEmail,
    successNotification,
  ]);

  return (
    <ActionButtonGroupRoot>
      {isLoading && (
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.5rem' }}>
          <div className="spinner" />
          <p>Creating users...</p>
        </div>
      )}
      <FormGroup row style={{ marginTop: '1rem', fontSize: '0.5rem' }}>
        <FormControlLabel
          control={
            <Checkbox
              name="add-users-no-email"
              color="primary"
              onClick={event => setSkipEmail((event.target as HTMLInputElement).checked)}
              disabled={isLoading}
            />
          }
          label={<Typography variant="body2">{ADD_USERS_NO_EMAIL}</Typography>}
        />
      </FormGroup>
      <div className={actionButtonGroupClasses.buttonGroup}>
        <Button
          variant="outlined"
          color="primary"
          style={{ marginRight: '1rem' }}
          onClick={() => handleDialogClose()}
          disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" disabled={disableCreate} onClick={handleSave}>
          {createButtonText}
        </Button>
      </div>
    </ActionButtonGroupRoot>
  );
};

export default ActionButtonGroup;
